import React, { useState, useEffect } from "react";
import { FaRegCalendarAlt } from "react-icons/fa";
import InputFrame from "../atoms/InputFrame";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

interface DayPickerProps {
    label: string;
    errorMessage?: string;
    value?: string | null;
    onChange: (date: string) => void;
    className?: string;
    disabled?: boolean;
}

const DayPicker: React.FC<DayPickerProps> = ({ label, errorMessage, value, onChange, className, disabled = false }) => {
    const [showCalendar, setShowCalendar] = useState(false);
    const [inputValue, setInputValue] = useState(value || "");
    const [currentDate, setCurrentDate] = useState(new Date());
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    let textColor = "text-gray-900 dark:text-white";
    let focusColor = "focus:border-blue-600 dark:focus:border-blue-500";
    let labelDarkFocusColor = "peer-focus:dark:text-blue-500";

    if (errorMessage) {
        textColor = "text-red-700 dark:text-red-500";
        focusColor = "focus:border-red-600 dark:focus:border-red-500";
        labelDarkFocusColor = "peer-focus:dark:text-red-500";
    }

    useEffect(() => {
        if (value === null || value === undefined) {
            setInputValue('');
        } else if (value === '') {
            const today = getCurrentDate();
            setCurrentDate(new Date());
            setInputValue(today);
            onChange(today);
        } else {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
        }
    }, []);

    useEffect(() => {
        if (value) {
            const parsedDate = new Date(value.split('/').reverse().join('-'));
            if (!isNaN(parsedDate.getTime()) && parsedDate.toISOString() !== currentDate.toISOString()) {
                setCurrentDate(parsedDate);
                setInputValue(value);
            } else {
                const today = getCurrentDate();
                setCurrentDate(new Date());
                setInputValue(today);
            }
        }
    }, [value]);


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawInput = e.target.value.replace(/\D/g, '');
        let formattedValue = rawInput;

        if (rawInput.length > 2) {
            formattedValue = `${rawInput.slice(0, 2)}/${rawInput.slice(2)}`;
        }
        if (rawInput.length > 4) {
            formattedValue = `${rawInput.slice(0, 2)}/${rawInput.slice(2, 4)}/${rawInput.slice(4)}`;
        }

        setInputValue(formattedValue);

        if (/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/.test(formattedValue)) {
            onChange(formattedValue);
        }
    };

    const handleCalendarToggle = () => {
        if (!disabled) {
            setShowCalendar(!showCalendar);
        }
    };

    const selectDate = (day: number) => {
        const date = `${String(day).padStart(2, '0')}/${String(currentMonth + 1).padStart(2, '0')}/${currentYear}`;
        setInputValue(date);
        onChange(date);
        setShowCalendar(false);
    };

    const getCurrentDate = (): string => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleMonthChange = (direction: 'prev' | 'next') => {
        setCurrentDate((prevDate) => {
            const newDate = new Date(prevDate);
            newDate.setMonth(prevDate.getMonth() + (direction === 'prev' ? -1 : 1));
            return newDate;
        });
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedYear = parseInt(e.target.value, 10);
        setCurrentDate((prevDate) => {
            const newDate = new Date(prevDate);
            newDate.setFullYear(selectedYear);
            return newDate;
        });
    };


    const getDaysInMonth = (year: number, month: number) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const renderDays = () => {
        const daysInMonth = getDaysInMonth(currentYear, currentDate.getMonth());
        const firstDayOfWeek = new Date(currentYear, currentDate.getMonth(), 1).getDay();

        const placeholders = Array.from({ length: firstDayOfWeek }, (_, i) => (
            <div key={`placeholder-${i}`} className="p-2 text-sm text-gray-300 dark:text-gray-600"></div>
        ));

        const days = Array.from({ length: daysInMonth }, (_, i) => (
            <button
                key={`day-${i + 1}`}
                className="p-2 text-sm rounded hover:bg-blue-200 dark:hover:bg-blue-700"
                onClick={() => selectDate(i + 1)}
            >
                {i + 1}
            </button>
        ));

        return [...placeholders, ...days];
    };


    const renderYearOptions = () => {
        const yearRange = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

        return yearRange.map((year) => (
            <option key={year} value={year}>
                {year}
            </option>
        ));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
        const value = e.currentTarget.value;

        if (allowedKeys.includes(e.key)) return;

        if (!/^\d$/.test(e.key) || value.length >= 10) {
            e.preventDefault();
            return;
        }
    };


    return (
        <div className={`relative ${className}`}>
            <InputFrame
                label={label}
                errorMessage={errorMessage}
                className="mb-4"
                textColor={textColor}
                labelClassName={`dark:text-gray-400 ${labelDarkFocusColor}`}
                prefix={
                    <FaRegCalendarAlt
                        className={`text-lg ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 cursor-pointer'}`}
                        onClick={handleCalendarToggle}
                    />
                }
            >
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    className={`block py-2.5 px-0 w-full text-sm ${textColor} bg-transparent border-0 border-b-2 border-gray-300 dark:border-gray-600 appearance-none focus:outline-none focus:ring-0 ${focusColor} peer disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={disabled}
                    placeholder="DD/MM/YYYY"
                />
            </InputFrame>

            {showCalendar && (
                <div className="absolute bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg mt-1 p-4 z-50 w-64">
                    <div className="flex items-center justify-between mb-2">
                        <button
                            onClick={() => handleMonthChange('prev')}
                            className="p-2 border border-transparent hover:border-blue-500 dark:hover:border-blue-500 rounded cursor-pointer text-gray-700 dark:text-gray-200"
                        >
                            <IoIosArrowBack />
                        </button>
                        <div className="flex items-center gap-2 max-w-40 min-w-40">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{currentDate.toLocaleString('pt-BR', { month: 'long' }).toLocaleUpperCase()}</span>
                            <select
                                value={currentYear}
                                onChange={handleYearChange}
                                className="text-sm border border-gray-300 dark:border-gray-600 rounded appearance-none pr-4 w-16 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                {renderYearOptions()}
                            </select>
                        </div>
                        <button
                            onClick={() => handleMonthChange('next')}
                            className="p-2 border border-transparent hover:border-blue-500 dark:hover:border-blue-500 rounded cursor-pointer text-gray-700 dark:text-gray-200"
                        >
                            <IoIosArrowForward />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 text-center gap-1 text-gray-900 dark:text-white">
                        {renderDays()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DayPicker;
